import { useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { DashboardLayout } from '../dashboard/components/DashboardLayout';
import { DashboardNav } from '../dashboard/components/DashboardNav';
import { DashboardHeader } from '../dashboard/components/DashboardHeader';
import { ConfirmModal } from '../../components/ConfirmModal';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { LogoutIcon } from '../../assets/LogoutIcon';
import './OrgSettingsLayout.css';

/**
 * OrgSettingsLayout wraps organization settings routes.
 * Accessible to org admins (role: 'admin') and system admins.
 * It provides the dashboard shell (nav, header) and renders child routes via Outlet.
 */
export const OrgSettingsLayout = () => {
  const { user, logout } = useAuth();
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const handleLogoutConfirm = async () => {
    try {
      await logout();
      setShowLogoutModal(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleLogoutCancel = () => {
    setShowLogoutModal(false);
  };

  return (
    <DashboardLayout>
      <DashboardNav
        onLogout={handleLogoutClick}
      />
      <div className="dashboard-content">
        <DashboardHeader
          title="Organization Settings"
          userEmail={user?.email || 'Unknown'}
        />
        {/*
          .dashboard-section-container carries no padding — every section supplies its own via
          .dashboard-section. Settings pages were not, which left their content flush against the
          viewport edge while the header above it was inset. One class here keeps every settings
          page aligned with that header instead of each remembering to pad itself.
        */}
        <main
          className="dashboard-section-container settings-page"
          id="main-content"
          tabIndex={-1}
        >
          <ErrorBoundary
            fallbackTitle="Settings Error"
            fallbackMessage="This section encountered an error. Try refreshing the page."
          >
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>

      <ConfirmModal
        isOpen={showLogoutModal}
        onClose={handleLogoutCancel}
        onConfirm={handleLogoutConfirm}
        title="Logout"
        message="Are you sure you want to logout?"
        confirmText="Yes, Logout"
        cancelText="Cancel"
        icon={<LogoutIcon />}
      />
    </DashboardLayout>
  );
};
